// Sticky topbar strip that shows the live Matrix broker bar.
// Operator request (2026-07-03): the strip carries ONLY the four core
// metrics — Total / RPM / OK / Avg. Per-source (Trade/PriceW/Settle/Bal/...)
// and error-bucket (403/429/...) breakdowns live in /matrix, not here.
// Polls every 10 s via `useMatrixStats` and pauses when the tab is hidden.
import { useMatrixStats } from "../hooks";
import type { AggregatedMatrixStats, MatrixStatsSummary } from "../api";

function formatMetric(value: number | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "--";
  return value < 1 && value > 0
    ? value.toFixed(1)
    : Math.round(value).toString();
}

function CoreStats({
  summary,
}: {
  summary: MatrixStatsSummary;
}): React.JSX.Element {
  const okClass = summary.successRate < 95 ? "text-warning" : "text-success";
  return (
    <>
      <span>
        Total{" "}
        <b className="font-mono text-foreground/80">
          {formatMetric(summary.total)}
        </b>
      </span>
      <span>
        RPM{" "}
        <b className="font-mono text-brand-cyan">
          {summary.reqPerMin.toFixed(1)}/min
        </b>
      </span>
      <span>
        OK{" "}
        <b className={`font-mono ${okClass}`}>
          {summary.successRate.toFixed(1)}%
        </b>
      </span>
      <span>
        Avg{" "}
        <b className="font-mono text-foreground/80">
          {summary.avgResponseMs}ms
        </b>
      </span>
    </>
  );
}

function BarBody({
  stats,
}: {
  stats: AggregatedMatrixStats;
}): React.JSX.Element | null {
  // `summary` carries a default_factory at the backend so it's always emitted,
  // but the OpenAPI schema marks it optional. Bail out cleanly if it's missing
  // so the bar shows the loading placeholder instead of crashing.
  if (!stats.summary) return null;
  return (
    <div className="flex flex-wrap items-center justify-end gap-3 text-[10px] uppercase tracking-wide text-foreground/70">
      <CoreStats summary={stats.summary} />
    </div>
  );
}

function BarPlaceholder(props: { label: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-end gap-3 text-[10px] uppercase tracking-wide text-muted-foreground">
      <span data-testid="bar-placeholder">{props.label}</span>
    </div>
  );
}

export function MatrixRateLimitBar(): React.JSX.Element {
  const { data, isError, isLoading } = useMatrixStats("1h");
  return (
    <div className="border-t border-border" data-testid="admin-rate-limit-bar">
      <div className="flex h-7 min-w-0 items-center justify-between px-3 text-[10px] uppercase tracking-wide">
        <span className="text-muted-foreground">Matrix (1h)</span>
        {isError ? (
          <BarPlaceholder label="Offline" />
        ) : !data ? (
          <BarPlaceholder label={isLoading ? "Carregando..." : "--"} />
        ) : (
          <BarBody stats={data} />
        )}
      </div>
    </div>
  );
}
